import { useCallback, useEffect, useMemo, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  MessageReviewConfigDTO,
  MessageReviewConfigUpdateParams,
  MessageReviewProvider,
  MessageReviewReasoningEffort,
  MessageReviewTestResultDTO,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

const DEFAULT_TIMEOUT_SECONDS = 60;
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_CONFIDENCE_THRESHOLD = 0.85;
const DEFAULT_LOCAL_OCR_BASE_URL = 'http://127.0.0.1:19090';
const DEFAULT_LOCAL_OCR_MAX_CONCURRENCY = 2;
const DEFAULT_LOCAL_OCR_CPU_THREADS = 3;
const DEFAULT_LOCAL_MODEL = 'pp-ocrv6-medium-opencv-hsv';

const EMPTY_FORM: MessageReviewConfigUpdateParams = {
  enabled: false,
  provider: 'openai',
  model: '',
  reasoning_effort: 'default',
  manual_review_ratio: 0,
  api_key: '',
  local_ocr_base_url: DEFAULT_LOCAL_OCR_BASE_URL,
  local_ocr_max_concurrency: DEFAULT_LOCAL_OCR_MAX_CONCURRENCY,
  local_ocr_cpu_threads: DEFAULT_LOCAL_OCR_CPU_THREADS,
  timeout_seconds: DEFAULT_TIMEOUT_SECONDS,
  max_retries: DEFAULT_MAX_RETRIES,
  confidence_threshold: DEFAULT_CONFIDENCE_THRESHOLD,
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid message review config': '短信审核配置无效，请检查当前 provider、模型、服务地址和阈值',
      message_review_config_invalid: '短信审核配置无效',
      message_review_not_configured: '尚未保存可用的 OpenAI API Key',
      message_review_provider_unavailable: 'OpenAI 审核 Provider 暂不可用',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

function resolveStableTestCode(e: unknown): string {
  if (!isApiError(e)) return 'request_failed';
  const allowedCodes = new Set([
    'internal server error',
    'message_review_config_invalid',
    'message_review_not_configured',
    'message_review_provider_unavailable',
    'network_error',
  ]);
  return allowedCodes.has(e.message) ? e.message : 'test_failed';
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function normalizeRatio(value: number): number {
  return Math.round(clamp(value, 0, 1) * 1000) / 1000;
}

function normalizeConcurrency(value: number | undefined): number {
  return Math.trunc(clamp(value ?? DEFAULT_LOCAL_OCR_MAX_CONCURRENCY, 1, 8));
}

function modelBelongsToProvider(provider: MessageReviewProvider, model: string): boolean {
  if (provider === 'local_screenshot') return model.startsWith('pp-ocrv6-');
  return model.startsWith('gpt-');
}

function pickDefaultModel(dto: MessageReviewConfigDTO): string {
  const provider = dto.provider || 'openai';
  if (dto.model && dto.capabilities?.[dto.model] && modelBelongsToProvider(provider, dto.model)) return dto.model;
  const fallback = provider === 'local_screenshot' ? DEFAULT_LOCAL_MODEL : '';
  if (fallback && dto.capabilities?.[fallback]) return fallback;
  if (dto.model && dto.capabilities?.[dto.model]) return dto.model;
  return dto.allowed_models.find((model) => dto.capabilities?.[model] && modelBelongsToProvider(provider, model)) ?? '';
}

function getReasoningEfforts(dto: MessageReviewConfigDTO | null, model: string): MessageReviewReasoningEffort[] {
  const efforts = dto?.capabilities?.[model]?.reasoning_efforts;
  if (Array.isArray(efforts) && efforts.length > 0) return efforts;
  return ['default'];
}

function dtoToForm(dto: MessageReviewConfigDTO): MessageReviewConfigUpdateParams {
  const model = pickDefaultModel(dto);
  const efforts = getReasoningEfforts(dto, model);
  const reasoning = efforts.includes(dto.reasoning_effort) ? dto.reasoning_effort : 'default';
  return {
    enabled: Boolean(dto.enabled),
    provider: dto.provider || 'openai',
    model,
    reasoning_effort: reasoning,
    manual_review_ratio: normalizeRatio(dto.manual_review_ratio ?? 0),
    api_key: '',
    local_ocr_base_url: dto.local_ocr_base_url || dto.base_url || DEFAULT_LOCAL_OCR_BASE_URL,
    local_ocr_max_concurrency: normalizeConcurrency(dto.local_ocr_max_concurrency),
    local_ocr_cpu_threads: dto.local_ocr_cpu_threads || DEFAULT_LOCAL_OCR_CPU_THREADS,
    timeout_seconds: Math.trunc(clamp(dto.timeout_seconds ?? DEFAULT_TIMEOUT_SECONDS, 5, 300)),
    max_retries: Math.trunc(clamp(dto.max_retries ?? DEFAULT_MAX_RETRIES, 0, 10)),
    confidence_threshold: normalizeRatio(dto.confidence_threshold ?? DEFAULT_CONFIDENCE_THRESHOLD),
  };
}

export function useMessageReviewConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<MessageReviewConfigDTO | null>(null);
  const [form, setForm] = useState<MessageReviewConfigUpdateParams>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<MessageReviewTestResultDTO | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getMessageReviewConfig();
      setData(result);
      setForm(dtoToForm(result));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const modelOptions = useMemo(() => {
    if (!data) return [];
    return data.allowed_models.filter((model) => Boolean(data.capabilities?.[model]) && modelBelongsToProvider(form.provider, model));
  }, [data, form.provider]);

  const reasoningOptions = useMemo(() => getReasoningEfforts(data, form.model), [data, form.model]);

  const updateField = useCallback(<K extends keyof MessageReviewConfigUpdateParams>(
    key: K,
    value: MessageReviewConfigUpdateParams[K],
  ) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'provider') {
        const provider = value as MessageReviewProvider;
        const models = data?.allowed_models.filter((model) => Boolean(data.capabilities?.[model]) && modelBelongsToProvider(provider, model)) ?? [];
        const nextModel = models.includes(prev.model) ? prev.model : provider === 'local_screenshot' && models.includes(DEFAULT_LOCAL_MODEL) ? DEFAULT_LOCAL_MODEL : models[0] ?? '';
        next.model = nextModel;
        next.reasoning_effort = 'default';
      }
      if (key === 'model') {
        const efforts = getReasoningEfforts(data, String(next.model));
        next.reasoning_effort = efforts.includes(prev.reasoning_effort) ? prev.reasoning_effort : 'default';
      }
      return next;
    });
    setTestResult(null);
  }, [data]);

  const save = useCallback(async () => {
    if (!Number.isInteger(form.local_ocr_cpu_threads) || form.local_ocr_cpu_threads < 1 || form.local_ocr_cpu_threads > 32) {
      addToast('error', 'CPU 线程数必须为 1～32 的整数');
      return false;
    }
    if (!form.model || !modelOptions.includes(form.model)) {
      addToast('error', '请选择服务端允许的审核模型');
      return false;
    }
    if (!reasoningOptions.includes(form.reasoning_effort)) {
      addToast('error', '请选择当前模型支持的 reasoning 选项');
      return false;
    }
    const normalized: MessageReviewConfigUpdateParams = {
      ...form,
      provider: form.provider,
      api_key: form.api_key.trim(),
      local_ocr_base_url: form.local_ocr_base_url.trim(),
      local_ocr_max_concurrency: normalizeConcurrency(form.local_ocr_max_concurrency),
      timeout_seconds: Math.trunc(clamp(form.timeout_seconds, 5, 300)),
      max_retries: Math.trunc(clamp(form.max_retries, 0, 10)),
      confidence_threshold: normalizeRatio(form.confidence_threshold),
      manual_review_ratio: normalizeRatio(form.manual_review_ratio),
    };

    setSaving(true);
    try {
      const result = await settingsService.updateMessageReviewConfig(normalized);
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', '短信审核配置已保存');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, form, modelOptions, reasoningOptions]);

  const testConnection = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await settingsService.testMessageReviewConfig();
      setTestResult(result);
      addToast(result.success ? 'success' : 'error', result.success ? '连接测试完成' : `连接测试失败 (${result.code})`);
      return result;
    } catch (e) {
      const fallback = { success: false, code: resolveStableTestCode(e) };
      setTestResult(fallback);
      addToast('error', `连接测试失败 (${fallback.code})`);
      return fallback;
    } finally {
      setTesting(false);
    }
  }, [addToast]);

  return {
    data,
    form,
    loading,
    saving,
    testing,
    error,
    modelOptions,
    reasoningOptions,
    testResult,
    updateField,
    save,
    testConnection,
    reload: load,
  };
}
